// Types
type MonthlyMetric = {
	Month: string
	Currency: string
	Value: number
	Region?: string
}

type MonthlyMetricsResponse = MonthlyMetric[]

type ApiEndpoint = {
	name: string
	url: string
}

type Environment = "dev" | "prod"

type MetricQueryParams = {
	start?: string
	end?: string
	period?: string
}

class API {
	convertMetrics: ConvertMetricsAPI
	url: (path: string) => string
	private environment: Environment = "prod"

	constructor() {
		this.convertMetrics = new ConvertMetricsAPI(this)
		this.url = (path) => apiBaseUrls()[0].url + path
	}

	setEnvironment(env: Environment) {
		this.environment = env
	}

	getEnvironment(): Environment {
		return this.environment
	}

	getApiEndpoints(): ApiEndpoint[] {
		return apiBaseUrls(this.environment)
	}
}

function RelativeURL(path: string) {
	const defaultBase = "https://path"
	const url = new URL(path, defaultBase)

	function appendQueryParam(name: string, value: string) {
		const newURL = new URL(url)
		newURL.searchParams.append(name, value)

		return RelativeURL(newURL.toString().replace(defaultBase, ""))
	}

	function toString() {
		return url.toString().replace(defaultBase, "")
	}

	return {appendQueryParam, toString}
}

class ConvertMetricsAPI {
	private api: API

	constructor(api: API) {
		this.api = api
	}

	async getMonthlyCommissionAmount(params?: MetricQueryParams): Promise<MonthlyMetricsResponse> {
		return aggregateMetricsFromAllEndpoints("/convert-metrics/commission", this.api.getApiEndpoints(), params)
	}

	async getMonthlyPaymentAmount(params?: MetricQueryParams): Promise<MonthlyMetricsResponse> {
		return aggregateMetricsFromAllEndpoints("/convert-metrics/payment-amount", this.api.getApiEndpoints(), params)
	}

	async getMonthlyUniqueCustomer(params?: MetricQueryParams): Promise<MonthlyMetricsResponse> {
		return aggregateMetricsFromAllEndpoints("/convert-metrics/unique-customer", this.api.getApiEndpoints(), params)
	}

	async getMonthlyStripeFees(params?: MetricQueryParams): Promise<MonthlyMetricsResponse> {
		return aggregateMetricsFromAllEndpoints("/convert-metrics/stripe-fees", this.api.getApiEndpoints(), params)
	}
}

async function aggregateMetricsFromAllEndpoints(
	path: string,
	endpoints: ApiEndpoint[],
	params?: MetricQueryParams,
): Promise<MonthlyMetricsResponse> {
	const allMetrics: MonthlyMetric[] = []

	// Build query string if params are provided
	let queryString = ""
	if (params) {
		const queryParams = new URLSearchParams()
		if (params.start) queryParams.append("start", params.start)
		if (params.end) queryParams.append("end", params.end)
		if (params.period) queryParams.append("period", params.period)
		const qs = queryParams.toString()
		if (qs) queryString = "?" + qs
	}

	// Fetch from all endpoints in parallel
	const results = await Promise.allSettled(
		endpoints.map(async (endpoint) => {
			try {
				const metrics = await getFromUrl(endpoint.url + path + queryString)
				// Add region information to each metric
				return metrics.map((metric: MonthlyMetric) => ({
					...metric,
					Region: endpoint.name,
				}))
			} catch (error) {
				console.warn(`Failed to fetch from ${endpoint.name}:`, error)
				return []
			}
		}),
	)

	// Aggregate successful results
	results.forEach((result) => {
		if (result.status === "fulfilled") {
			allMetrics.push(...result.value)
		}
	})

	// Group by Month and Currency, summing Values
	const aggregated = allMetrics.reduce(
		(acc, metric) => {
			const key = `${metric.Month}|${metric.Currency}`
			if (!acc[key]) {
				acc[key] = {
					Month: metric.Month,
					Currency: metric.Currency,
					Value: 0,
					Regions: [] as string[],
				}
			}
			acc[key].Value += metric.Value
			if (metric.Region && !acc[key].Regions.includes(metric.Region)) {
				acc[key].Regions.push(metric.Region)
			}
			return acc
		},
		{} as Record<
			string,
			{
				Month: string
				Currency: string
				Value: number
				Regions: string[]
			}
		>,
	)

	// Convert back to array and add Region as comma-separated list
	return Object.values(aggregated).map((item) => ({
		Month: item.Month,
		Currency: item.Currency,
		Value: item.Value,
		Region: item.Regions.join(", "),
	}))
}

function getFromUrl(fullUrl: string, options = {}) {
	const url = RelativeURL(fullUrl).toString()

	return new Promise<any>((resolve, reject) => {
		fetch(url, {...options, method: "GET"})
			.then((res) => {
				const contentType = res.headers.get("Content-Type")
				const isJSON = contentType && contentType.includes("application/json")
				const promise = isJSON ? res.json() : res.text()
				promise.then((body) => (res.status >= 400 ? reject(apiError(body)) : resolve(body)))
			})
			.catch(() => {
				reject(apiError("Network error"))
			})
	})
}

function apiBaseUrls(environment: Environment = "prod"): ApiEndpoint[] {
	const urls: ApiEndpoint[] = []

	const env = import.meta.env

	if (environment === "dev" && env.VITE_DEV_API_URL) {
		urls.push({name: "DEV", url: env.VITE_DEV_API_URL})
		return urls
	}

	// For prod, include all prod endpoints
	if (env.VITE_PROD_AU_API_URL) {
		urls.push({name: "AU", url: env.VITE_PROD_AU_API_URL})
	}
	if (env.VITE_PROD_US_API_URL) {
		urls.push({name: "US", url: env.VITE_PROD_US_API_URL})
	}
	if (env.VITE_PROD_AE_API_URL) {
		urls.push({name: "AE", url: env.VITE_PROD_AE_API_URL})
	}
	if (env.VITE_PROD_BR_API_URL) {
		urls.push({name: "BR", url: env.VITE_PROD_BR_API_URL})
	}

	return urls
}

function apiError(error: unknown) {
	const unknownError = {Message: "Unknown error"}
	if (!error) return unknownError
	if (typeof error == "string") return {Message: error}
	if (typeof error === "object") {
		if ("message" in error && typeof error.message === "string") {
			return {Message: error.message}
		}
		if ("Message" in error) return error as {Message: string}
		if ("Errors" in error && typeof error.Errors === "object" && error.Errors !== null) {
			const message = Object.keys(error.Errors)
				.map((key) => {
					const errorValue = (error.Errors as Record<string, unknown>)[key]
					if (Array.isArray(errorValue)) {
						return errorValue.map((msg) => `${key} ${msg}`)
					}
					return []
				})
				.flat()
				.join(".")
			return {Message: message}
		}
	}
	return unknownError
}

const api = new API()

export {api}
export type {MonthlyMetric, MonthlyMetricsResponse, Environment, MetricQueryParams}
